import type { RaceSize } from '../gen/modes';
import type { RoomMode, ServerMessage } from '../meta/online/live';
import type { Seat } from '../meta/online/race';
import type { LineState } from '../platform/live';

/** A live race's room as the screens see it (docs/LIVE.md), and how each message changes it. */

type RoomMessage = Extract<ServerMessage, { type: 'room' }>;
export type RacePlayer = RoomMessage['players'][number];
export type RaceResult = Extract<ServerMessage, { type: 'result' }>;
/** Head to head's result (docs/HEAD_TO_HEAD.md). */
export type MatchResult = Extract<ServerMessage, { type: 'match' }>;

export interface RaceView {
  code: string;
  /** The line to the room; `closed` with {@link RaceView.closedWhy}. */
  line: LineState;
  closedWhy: string | null;
  /** This phone's seat (0: the inviter), once the room has said. */
  you: Seat | null;
  size: RaceSize | null;
  players: RacePlayer[];
  /** The room's phase, once it has said. */
  phase: RoomMessage['phase'] | null;
  /** The race's lawn and when its first tick is, on this phone's clock. */
  start: { seed: string; atLocal: number } | null;
  result: RaceResult | null;
  /** What the room plays: a race, or head to head (null until the room says). */
  mode: RoomMode | null;
  /** The inviter's rules version, which a match plays by (null: not said, a room before 9). */
  rules: number | null;
  /** How many the room seats: two, or a party of up to four (docs/PARTY.md). */
  seats: number;
  /** Head to head's result, once the room has judged the match. */
  match: MatchResult | null;
  /** The friend left the room for good. */
  friendLeft: boolean;
  /** The room refused something (shown, then forgotten on the next change). */
  problem: string | null;
}

/** The view after a message from the room, which arrived at `now` (this phone's clock). Pure. */
export function nextView(view: RaceView, message: ServerMessage, now: number): RaceView {
  switch (message.type) {
    case 'room':
      return {
        ...view,
        you: message.you,
        size: message.size,
        players: message.players,
        phase: message.phase,
        mode: message.mode ?? 'race',
        rules: message.rules ?? null,
        seats: message.seats ?? 2,
        // Waiting again (a new friend, or the old one gone): nothing to start from.
        ...(message.phase === 'waiting' ? { start: null, result: null, match: null } : {}),
        problem: null,
      };
    case 'countdown':
      return {
        ...view,
        // On this phone's clock: when the message came, plus how long the room said was left.
        start: { seed: message.seed, atLocal: now + (message.at - message.server) },
        result: null,
        // (The same start sent again after a reconnect keeps the match's result, if any.)
        match: message.seed === view.start?.seed ? view.match : null,
        friendLeft: false,
      };
    case 'result':
      return { ...view, result: message };
    case 'match':
      return { ...view, match: message };
    case 'left':
      return message.seat === view.you ? view : { ...view, friendLeft: true };
    case 'error':
      return { ...view, problem: message.problem };
    default:
      return view;
  }
}

/** What the friend's phone has said during this race: turns, and how far it has got. */
export interface FriendTrack {
  /** Their turns, `[tick, intent code]`, by number (a gap: one still on its way). */
  inputs: ([number, number] | undefined)[];
  /**
   * Their recent progress, `[tick, turns before it]`, oldest first: their ghost may run to a tick
   * once every turn before it has arrived.
   */
  marks: [number, number][];
  pct: number;
  /** Their state's hash at recent ticks, `[tick, hash]` (the out-of-sync check), oldest first. */
  hashes: [number, string][];
}

/**
 * How far the friend's ghost may run: the latest progress tick whose turns have all arrived (a gap
 * in the turns holds it back until the missing one comes).
 */
export function safeTick(track: FriendTrack): number {
  let have = 0;
  while (track.inputs[have] !== undefined) have++;
  let tick = 0;
  for (const [t, before] of track.marks) if (before <= have && t > tick) tick = t;
  return tick;
}
